import { FC, useState } from 'react'

import { SuggestionDto } from '@api'

import { Tooltip } from '@/components/ui/tooltip'
import { SuggestionDtoWithTranslation } from '@/models/chattingUI/chatSuggestions/chatSuggestions.ts'

import { ChatFeedbackBlock } from './ChatFeedbackBlock'
import { ChatSuggestionMessageAction } from './ChatSuggestionMessageAction'
import { ChatSuggestionMetadata } from './ChatSuggestionMetadata'
import { ChatSuggestionScoring } from './ChatSuggestionScoring'

interface Props {
  message: SuggestionDtoWithTranslation
  messageNumber: number
  selectedScore: Record<number, number>
  step: 'view' | 'scoring' | 'feedback'
  onScoreSelect: (index: number, score: number) => void
  onFeedbackChange: (index: number, feedback: string) => void
  onChangeStep: (step: 'view' | 'scoring' | 'feedback') => void
  onSend: (message: SuggestionDto, index: number) => void
  submitDisabledReason: string
  onOpenMediaPreview: () => void
}

export const ChatSuggestionMessage: FC<Props> = ({
  onScoreSelect,
  onFeedbackChange,
  onChangeStep,
  onSend,
  message,
  messageNumber,
  selectedScore,
  step,
  submitDisabledReason,
  onOpenMediaPreview,
}) => {
  const [feedback, setFeedback] = useState('')
  const primaryMessage = message?.messages?.[0]

  const handleSend = () => {
    if (step === 'view') {
      onChangeStep('scoring')
    } else if (step === 'scoring') {
      onSend(message, messageNumber)
    }
  }

  return (
    <div className="flex  gap-3">
      <div className="rounded-lg border border-border flex items-center flex-1 overflow-hidden">
        <div className="p-4 flex items-center gap-2.5 w-full overflow-hidden">
          <ChatSuggestionMetadata
            metadata={message}
            onOpenMediaPreview={onOpenMediaPreview}
          />
          <Tooltip
            content={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    primaryMessage?.translatedText ||
                    primaryMessage?.text ||
                    '',
                }}
              />
            }
            contentClassName="max-w-xs leading-normal"
            asChild
          >
            <span
              className="text-sm font-medium text-text-default text-nowrap truncate flex-1 overflow-ellipsis"
              dangerouslySetInnerHTML={{
                __html:
                  primaryMessage?.translatedText || primaryMessage?.text || '',
              }}
            />
          </Tooltip>
        </div>
        {step === 'scoring' && (
          <ChatSuggestionScoring
            onSelect={(score) => onScoreSelect(messageNumber, score)}
            selectedScore={selectedScore[messageNumber]}
          />
        )}
        {step !== 'feedback' && (
          <ChatSuggestionMessageAction
            onSend={handleSend}
            disabled={!!submitDisabledReason}
            disabledReason={submitDisabledReason}
          />
        )}
      </div>
      {step === 'feedback' && (
        <ChatFeedbackBlock
          value={feedback}
          onChange={(value) => {
            setFeedback(value)
            onFeedbackChange(messageNumber, value)
          }}
        />
      )}
    </div>
  )
}
